/**
 * อ่านเมนูจากแชท: the recheck modal (one at a time) and the expanded menu picker (ลงออเดอร์ › 2 แพ็กและเมนู › ขยาย).
 * start(source) opens the modal READY (owner 28 ก.ย. 12:52 "บางทีมีบางอย่างที่ AI ต้องรู้ก่อนอ่าน": the staff may write
 * a note first) and read() asks the API when they press "อ่านเมนู"; the staff fix the list; confirm() replaces the menus of the
 * order tab (+ the pack chosen in the modal), switches to that tab and says so in a toast. Nothing reaches the customer.
 * Round 2: the owner's switch (`enabled`, Config › AI "อ่านเมนูจากแชท" — off hides the bubble buttons) and
 * "สุ่มเมนูที่เหลือ N กล่อง" with type chips pre-selected from the read's `mix_hint`.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError } from '../api/client'
import { getMenuReadApi, type MenuReadBody, type MenuReadResult } from '../api/menuread'
import type { PosMenu } from '../api/pos/types'
import {
  addRow,
  allowFromHint,
  applyFill,
  doneToast,
  kindCounts,
  kindOf,
  packLine,
  randomFill,
  randomPool,
  removeRow,
  rowsFrom,
  setRowQty,
  sortRows,
  tally,
  toCartItems,
  unfill,
  type RcRow,
  type ReadSource,
  type SortMode,
} from '../lib/menuRead'
import { useInbox } from './inbox'
import { useOrders } from './order'

const SORT_KEY = 'cf_menuread_sort'
const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))

function savedSort(): SortMode {
  try {
    return localStorage.getItem(SORT_KEY) === 'cust' ? 'cust' : 'p'
  } catch {
    return 'p'
  }
}

export const useMenuRead = defineStore('menuRead', () => {
  const orders = useOrders()

  const src = ref<ReadSource | null>(null)
  /** ready = the modal is open, nothing read yet (the staff press "อ่านเมนู"). */
  const phase = ref<'idle' | 'ready' | 'reading' | 'ok' | 'error'>('idle')
  const error = ref<string | null>(null)
  const result = ref<MenuReadResult | null>(null)
  const rows = ref<RcRow[]>([])
  const sort = ref<SortMode>(savedSort())
  const note = ref('')
  /** The pack chosen in the modal (a new pack; a chat continuing an open pack keeps it). */
  const packId = ref<number | null>(null)
  /** The pack the read suggested from what the customer said (marked in the picker). */
  const suggestedId = ref<number | null>(null)
  /** Menus in the order form when the modal opened (the confirm replaces them). */
  const replacing = ref(0)
  let seq = 0

  // ------------------------------------------------ the order form's pack context
  const form = computed(() => (src.value ? (orders.forms[src.value.convId] ?? null) : null))
  /** Continuing an open pack: its name and the meals left (the order tab's quota). */
  const cont = computed(() => {
    const f = form.value
    if (!f?.continueHeaderId || !f.customerId) return null
    const p = orders.histories[f.customerId]?.data?.open_packs.find((x) => x.order_header_id === f.continueHeaderId)
    return p ? { name: `สั่งต่อ ${p.set_menu_name}`, left: p.meals_remaining } : { name: 'สั่งต่อแพ็กเดิม', left: 0 }
  })
  const pack = computed(() => orders.catalog?.set_menus.find((s) => s.id === packId.value) ?? null)
  const limit = computed<number | null>(() => (cont.value ? cont.value.left || null : (pack.value?.meals ?? null)))
  const counts = computed(() => tally(rows.value))
  const line = computed(() => packLine(counts.value, limit.value))
  const canConfirm = computed(() => phase.value === 'ok' && rows.value.length > 0 && (!!cont.value || packId.value !== null))

  // ------------------------------------------------ the owner's switch (Config › AI "อ่านเมนูจากแชท")
  /** null = not known yet (the buttons show: the API says 409 if it is off). */
  const enabled = ref<boolean | null>(null)
  let enabledAt = 0
  let enabledJob: Promise<void> | null = null
  /** Asks at most once a minute (every bubble calls it). */
  function checkEnabled(now = Date.now()): Promise<void> {
    if (enabledJob) return enabledJob
    if (enabled.value !== null && now - enabledAt < 60_000) return Promise.resolve()
    enabledAt = now
    enabledJob = getMenuReadApi()
      .enabled()
      .then((v) => {
        enabled.value = v
      })
      .catch(() => {
        /* keep what we knew: the read itself says so if it is off */
      })
      .finally(() => {
        enabledJob = null
      })
    return enabledJob
  }
  function setEnabled(v: boolean): void {
    enabled.value = v
    enabledAt = Date.now()
  }

  // ------------------------------------------------ "สุ่มเมนูที่เหลือ N กล่อง" (POS #btn-random)
  /** Chip "รวมเมนูบวกเงิน": off by default like the POS button; staff turn it on when the customer pays the extra. */
  const withPlus = ref(false)
  const pool = computed(() =>
    randomPool(orders.catalog?.menus ?? [], cont.value?.name ?? pack.value?.name ?? null, withPlus.value),
  )
  const kinds = computed(() => kindCounts(pool.value))
  /** Kinds the fill may use: null = all (chip "ทุกประเภท"); pre-selected from what the customer said. */
  const allow = ref<string[] | null>(null)
  const missing = computed(() => (limit.value ? Math.max(0, limit.value - counts.value.meals) : 0))
  const rndBoxes = computed(() => rows.value.reduce((a, r) => a + (r.rnd ?? 0), 0))
  /** Dishes the fill can use with the chips as they are. */
  const fillable = computed(() => pool.value.filter((m) => allow.value === null || allow.value.includes(kindOf(m))))
  function toggleKind(k: string | null): void {
    if (k === null) {
      allow.value = null
      return
    }
    const cur = allow.value ?? []
    allow.value = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]
  }
  /** Fills the boxes still missing in the pack; returns how many went in. */
  function fill(rng: () => number = Math.random): number {
    const adds = randomFill(rows.value, pool.value, missing.value, allow.value, rng)
    rows.value = applyFill(rows.value, adds, orders.catalog?.menus ?? [], sort.value)
    return adds.reduce((a, x) => a + x.qty, 0)
  }
  /** "สุ่มใหม่": the untouched random boxes out, then a new fill. */
  function refill(rng: () => number = Math.random): number {
    rows.value = unfill(rows.value)
    return fill(rng)
  }

  // ------------------------------------------------ modal
  function start(s: ReadSource): void {
    withPlus.value = false
    const f = orders.form(s.convId)
    src.value = s
    note.value = ''
    rows.value = []
    result.value = null
    packId.value = f.continueHeaderId ? null : f.setMenuId
    suggestedId.value = null
    allow.value = null
    replacing.value = f.items.length
    error.value = null
    phase.value = 'ready'
    seq++
    void orders.loadCatalog()
  }

  async function read(): Promise<void> {
    const s = src.value
    if (!s) return
    const my = ++seq
    phase.value = 'reading'
    error.value = null
    const body: MenuReadBody = { message_ids: s.ids, note: note.value.trim() }
    if (cont.value) {
      body.pack_limit = cont.value.left
      body.pack_name = cont.value.name
    } else if (packId.value !== null) body.set_menu_id = packId.value
    try {
      const res = await getMenuReadApi().read(s.convId, body)
      if (my !== seq) return
      result.value = res
      rows.value = sortRows(rowsFrom(res.items), sort.value)
      const sug = res.pack?.suggested ?? null
      suggestedId.value = sug && !cont.value ? sug.id : null
      // the customer named a pack: pre-select it (marked "แนะนำจากแชท"); the staff confirm or change it
      if (suggestedId.value !== null && orders.catalog?.set_menus.some((x) => x.id === suggestedId.value)) packId.value = suggestedId.value
      // "คละเอาแต่ไก่" / "ไม่เอาหมู": the random fill's chips as the customer said (the staff press the button)
      allow.value = allowFromHint(res.mix_hint, kinds.value.map((k) => k.k))
      phase.value = 'ok'
    } catch (e) {
      if (my !== seq) return
      error.value = errText(e) || 'อ่านเมนูไม่สำเร็จ'
      phase.value = 'error'
      if (e instanceof ApiError && e.status === 409) setEnabled(false) // the owner switched it off: the buttons go
    }
  }

  function close(): void {
    seq++
    src.value = null
    phase.value = 'idle'
    error.value = null
    result.value = null
    rows.value = []
  }

  function setQty(menuId: number, qty: unknown): void {
    rows.value = setRowQty(rows.value, menuId, qty)
  }
  function remove(menuId: number): void {
    rows.value = removeRow(rows.value, menuId)
  }
  function add(m: PosMenu): void {
    rows.value = addRow(rows.value, m, sort.value)
  }
  function toggleSort(): void {
    sort.value = sort.value === 'p' ? 'cust' : 'p'
    rows.value = sortRows(rows.value, sort.value)
    try {
      localStorage.setItem(SORT_KEY, sort.value)
    } catch {
      /* private mode */
    }
  }

  function confirm(): boolean {
    const s = src.value
    if (!s || !canConfirm.value) return false
    // a held order waiting for the transfer: the dishes go into its edit ("แก้ไขออเดอร์") or a new order the staff opened —
    // never into a form by themselves (owner 18 ต.ค. 69)
    const why = orders.fillBlock(s.convId, true)
    if (why) {
      useInbox().flash(why)
      return false
    }
    const t = counts.value
    orders.applyMenuRead(s.convId, { setMenuId: cont.value ? undefined : packId.value, items: toCartItems(rows.value) })
    const inbox = useInbox()
    inbox.panelTab = 'order'
    inbox.flash(doneToast(t))
    close()
    return true
  }

  /** The bubble whose read is running (its action shows "กำลังอ่านเมนู…"). */
  function busyFor(anchorId: string): boolean {
    return phase.value === 'reading' && src.value?.anchorId === anchorId
  }

  // ------------------------------------------------ expanded menu picker (split screen)
  const wideConv = ref<string | null>(null)
  // the side panel is rebuilt on "ย่อกลับ": where it was scrolled (the panel + the menu list) comes back with it
  // (owner 28 ก.ย.: it jumped to the top)
  type SideScroll = { conv: string; top: number; list: number }
  let sideScroll: SideScroll | null = null
  function expand(convId: string, at?: Omit<SideScroll, 'conv'>): void {
    sideScroll = at ? { conv: convId, ...at } : null
    wideConv.value = convId
  }
  /** Once, for the order tab of this chat that mounts after "ย่อกลับ". */
  function takeSideScroll(convId: string): Omit<SideScroll, 'conv'> | null {
    const s = sideScroll
    if (!s || s.conv !== convId) return null
    sideScroll = null
    return { top: s.top, list: s.list }
  }
  function collapse(): void {
    wideConv.value = null
  }
  // "เฉพาะที่เลือก" is one switch per chat for the small list and the expanded grid (owner 28 ก.ย. 22:17: ticking it
  // in one must hold in the other)
  const onlyPicked = ref<Record<string, boolean>>({})
  function toggleOnlyPicked(convId: string): void {
    onlyPicked.value = { ...onlyPicked.value, [convId]: !onlyPicked.value[convId] }
  }

  return {
    src,
    phase,
    error,
    result,
    rows,
    sort,
    note,
    packId,
    suggestedId,
    replacing,
    cont,
    pack,
    limit,
    counts,
    line,
    canConfirm,
    enabled,
    checkEnabled,
    setEnabled,
    pool,
    kinds,
    allow,
    withPlus,
    missing,
    rndBoxes,
    fillable,
    toggleKind,
    fill,
    refill,
    start,
    read,
    close,
    setQty,
    remove,
    add,
    toggleSort,
    confirm,
    busyFor,
    wideConv,
    expand,
    takeSideScroll,
    collapse,
    onlyPicked,
    toggleOnlyPicked,
  }
})
